import "@/index.css";
import "@/lib/i18n"; // Initialize i18n before anything else
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy, useState, useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OperationsProvider } from "@/contexts/OperationsContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";
import { ConfirmProvider } from "@/hooks/use-confirm";
import { Loader2 } from "lucide-react";

// ==================== LAZY-LOADED PAGES ====================
// Reduces initial bundle size — each page loaded on demand
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const CreatorPage = lazy(() => import("@/pages/CreatorPage"));
const StudioPage = lazy(() => import("@/pages/StudioPage"));
const SourcesPage = lazy(() => import("@/pages/SourcesPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const GlobalHistoryPage = lazy(() => import("@/pages/GlobalHistoryPage"));
const PdfLabPage = lazy(() => import("@/pages/PdfLabPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const LegalSearchPage = lazy(() => import("@/pages/LegalSearchPage"));
const KnowledgeBasePage = lazy(() => import("@/pages/KnowledgeBasePage"));
const ContractBenchmarkPage = lazy(() => import("@/pages/ContractBenchmarkPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const AgentPage = lazy(() => import("@/pages/AgentPage"));
const CalculatorsPage = lazy(() => import("@/pages/CalculatorsPage"));
const PredictionPage = lazy(() => import("@/pages/PredictionPage"));
const ContractAnalysisPage = lazy(() => import("@/pages/ContractAnalysisPage"));
const CourtPortalPage = lazy(() => import("@/pages/CourtPortalPage"));
const TemplatesPage = lazy(() => import("@/pages/TemplatesPage"));
const CompliancePage = lazy(() => import("@/pages/CompliancePage"));
const DueDiligencePage = lazy(() => import("@/pages/DueDiligencePage"));
const DeveloperPage = lazy(() => import("@/pages/DeveloperPage"));
const NegotiationPage = lazy(() => import("@/pages/NegotiationPage"));
const AdvicePage = lazy(() => import("@/pages/AdvicePage"));
const KnowledgeGraphPage = lazy(() => import("@/pages/KnowledgeGraphPage"));
const TrashPage = lazy(() => import("@/pages/TrashPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminReportsPage = lazy(() => import("@/pages/AdminReportsPage"));
const MyReportsPage = lazy(() => import("@/pages/MyReportsPage"));
const DetectivePage = lazy(() => import("@/pages/DetectivePage"));
const MecenasDetektywPage = lazy(() => import("@/pages/MecenasDetektywPage"));
const AdminInboxPage = lazy(() => import("@/pages/AdminInboxPage"));
const UserInboxPage = lazy(() => import("@/pages/UserInboxPage"));
const SuspendedPage = lazy(() => import("@/pages/SuspendedPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("@/pages/AcceptInvitePage"));
// 2026-05-07: legal stub pages (BUG-003 fix — RODO compliance)
const PolitykaPrywatnosciPage = lazy(() => import("@/pages/legal/PolitykaPrywatnosciPage"));
const RegulaminPage = lazy(() => import("@/pages/legal/RegulaminPage"));
const RodoPage = lazy(() => import("@/pages/legal/RodoPage"));
const BezpieczenstwoPage = lazy(() => import("@/pages/legal/BezpieczenstwoPage"));
// 2026-06-01: Makieta "wariant D" (Command Center) — izolowany, opt-in /cockpit, scoped theme. Klasyczny UI nietknięty.
const CockpitPreview = lazy(() => import("@/ui-cockpit/CockpitPreview"));

// Non-lazy (needed immediately for layout)
import AppShell from "@/components/AppShell";
import GlobalSearch from "@/components/GlobalSearch";
import OnboardingWizard from "@/components/OnboardingWizard";

// ==================== LOADING FALLBACK ====================
// Loader z bezpiecznym timeoutem. Bez tego, gdy wczytywanie utknie na dobre
// (kod strony nie doszedł z sieci albo sprawdzanie sesji wisi), użytkownik
// oglądał wieczny spinner bez żadnej informacji. Po kilkunastu sekundach
// pokazujemy czytelny komunikat i przycisk odświeżenia — bez obwiniania usera
// i z zapewnieniem, że dane są bezpieczne.
function PageLoader() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSlow(true), 20000);
    return () => clearTimeout(id);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      {slow ? (
        <>
          <p className="text-sm font-medium">Wczytywanie trwa dłużej niż zwykle.</p>
          <p className="text-sm text-muted-foreground max-w-sm">
            Sprawdź połączenie z internetem lub odśwież stronę. Twoje dane są bezpieczne.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-1 inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
          >
            Odśwież stronę
          </button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">LES-LEX — wczytywanie…</p>
      )}
    </div>
  );
}

function ProtectedRoute({ children, allowSuspended = false, allowMustChangePassword = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  if (!user) return <Navigate to="/login" replace />;
  // Strict === false check: undefined (existing users without field) passes through
  if (user.email_verified === false) {
    return <Navigate to="/verify-email" replace />;
  }
  if (!allowSuspended && user.account_status === "suspended") {
    return <Navigate to="/suspended" replace />;
  }
  if (!allowMustChangePassword && user.must_change_password) {
    return <Navigate to="/settings?force_password_change=1" replace />;
  }
  return children;
}

function RoleProtectedRoute({ children, allowedRoles, allowSuspended = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageLoader />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.email_verified === false) {
    return <Navigate to="/verify-email" replace />;
  }
  if (!allowSuspended && user.account_status === "suspended") {
    return <Navigate to="/suspended" replace />;
  }
  if (user.must_change_password) {
    return <Navigate to="/settings?force_password_change=1" replace />;
  }
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AppRoutes() {
  // Cockpit (/cockpit) ma własny scoped UI i nie powinien mieć globalnych overlayów
  // klasyka (GlobalSearch, OnboardingWizard) zasłaniających go. Reszta tras bez zmian.
  const loc = useLocation();
  const onCockpit = loc.pathname.startsWith("/cockpit");
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public pages */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<LoginPage />} />
          <Route path="/auth/google/callback" element={<LoginPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* FAZA 0 / Z10: akceptacja zaproszenia do kancelarii (link z e-maila).
              Strona publiczna — sama przekierowuje niezalogowanych na /login. */}
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/pricing" element={<PricingPage />} />

          {/* 2026-06-10: /cockpit/* (splat) — cockpit ma wewnetrzny routing stron (Sprawy itd.);
              CommandCenter wyprowadza widok z URL. onCockpit (startsWith) dalej tlumi globalne overlaye.
              2026-07-02: koniec "publicznego podgladu" — cockpit operuje na danych kancelarii
              (tajemnica adwokacka), wiec pelny ProtectedRoute jak klasyk (defense-in-depth;
              backend i tak wymaga JWT, ale UI nie moze byc anonimowo dostepny). */}
          <Route path="/cockpit/*" element={<ProtectedRoute><CockpitPreview /></ProtectedRoute>} />

          {/* Legal pages — public, RODO compliance (2026-05-07) */}
          <Route path="/polityka-prywatnosci" element={<PolitykaPrywatnosciPage />} />
          <Route path="/regulamin" element={<RegulaminPage />} />
          <Route path="/rodo" element={<RodoPage />} />
          <Route path="/bezpieczenstwo" element={<BezpieczenstwoPage />} />

          {/* Suspended account page */}
          <Route path="/suspended" element={
            <ProtectedRoute allowSuspended><SuspendedPage /></ProtectedRoute>
          } />

          {/* Settings — accessible when suspended or must_change_password */}
          <Route element={<ProtectedRoute allowSuspended allowMustChangePassword><AppShell /></ProtectedRoute>}>
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

          {/* Protected — Dashboard + Tools (blocked when suspended) */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/legal-search" element={<LegalSearchPage />} />
            <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="/benchmark" element={<ContractBenchmarkPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/calculators" element={<CalculatorsPage />} />
            <Route path="/prediction" element={<PredictionPage />} />
            <Route path="/contract-analysis" element={<ContractAnalysisPage />} />
            <Route path="/court-portal" element={<CourtPortalPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/detective" element={<DetectivePage />} />
            <Route path="/due-diligence" element={<DueDiligencePage />} />
            <Route path="/due-diligence/:ddId" element={<DueDiligencePage />} />
            <Route path="/negotiation" element={<NegotiationPage />} />
            <Route path="/negotiation/:sessionId" element={<NegotiationPage />} />
            <Route path="/advice" element={<AdvicePage />} />
            <Route path="/knowledge-graph" element={<KnowledgeGraphPage />} />
            <Route path="/history" element={<GlobalHistoryPage />} />
            <Route path="/trash" element={<TrashPage />} />
            <Route path="/inbox" element={<UserInboxPage />} />
            <Route path="/reports/my" element={<MyReportsPage />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["owner", "admin"]}><AppShell /></RoleProtectedRoute>}>
            <Route path="/developer" element={<DeveloperPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/reports" element={<AdminReportsPage />} />
            {/* Wywiad AI = intelligence service. Trusted users only.
                 2026-05-06: moved here from regular auth-only routes.
                 Reason: aggressive OSINT capabilities (web scraping, deep KRS, sankcje)
                 require admin-level trust + audit accountability. */}
            <Route path="/mecenas-detektyw" element={<MecenasDetektywPage />} />
          </Route>

          <Route element={<RoleProtectedRoute allowedRoles={["owner"]}><AppShell /></RoleProtectedRoute>}>
            <Route path="/admin/inbox" element={<AdminInboxPage />} />
          </Route>

          {/* Protected — Project views */}
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/project/:projectId/creator" element={<CreatorPage />} />
            <Route path="/project/:projectId/studio" element={<StudioPage />} />
            <Route path="/project/:projectId/sources" element={<SourcesPage />} />
            <Route path="/project/:projectId/history" element={<HistoryPage />} />
            <Route path="/project/:projectId/pdf-lab" element={<PdfLabPage />} />
            <Route path="/project/:projectId/agent" element={<AgentPage />} />
            <Route path="/project/:projectId" element={<Navigate to="creator" replace />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/* Global overlays — wyciszone na /cockpit (własny scoped UI). */}
      {!onCockpit && (
        <>
          <GlobalSearch />
          <OnboardingWizard />
        </>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <OperationsProvider>
            <ConfirmProvider>
              <AppRoutes />
              <Toaster position="bottom-right" richColors />
            </ConfirmProvider>
          </OperationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
